import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Logo from '../components/Logo';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AcceptInvitePage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [state, setState] = useState({ loading: true, invite: null, error: null });
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, invite: null, error: 'invalidToken' });
      return;
    }
    axios.get(`${BACKEND}/api/staff-invites/validate/${encodeURIComponent(token)}`)
      .then((r) => setState({ loading: false, invite: r.data, error: null }))
      .catch((e) => setState({ loading: false, invite: null, error: e.response?.data?.error || 'invalidToken' }));
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setErr(null);
    if (pwd.length < 8) { setErr(t('passwordReset.tooShort')); return; }
    if (pwd !== pwd2) { setErr(t('passwordReset.noMatch')); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/staff-invites/accept`, { token, password: pwd });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (e2) {
      setErr(e2.response?.data?.message || t('passwordReset.failed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md so-card p-8">
        <div className="flex justify-center mb-6"><Logo /></div>
        <h1 className="text-xl font-semibold text-text mb-2" data-testid="accept-invite-title">{t('staffInvite.acceptTitle')}</h1>

        {state.loading && <p className="text-sm text-text-secondary">{t('common.loading')}</p>}

        {!state.loading && state.error && (
          <>
            <div className="rounded-md bg-danger/10 border border-danger/30 p-4 mb-4" data-testid="accept-invite-error">
              <p className="text-sm font-semibold text-danger">{
                state.error === 'used' ? t('staffInvite.alreadyAccepted') :
                state.error === 'revoked' ? t('staffInvite.revoked') :
                state.error === 'expired' ? t('staffInvite.expired') :
                t('staffInvite.invalidToken')
              }</p>
              <p className="text-xs text-text-secondary mt-2">{t('staffInvite.contactInviter')}</p>
            </div>
            <Link to="/login" className="text-accent text-sm">{t('passwordReset.backToLogin')}</Link>
          </>
        )}

        {!state.loading && state.invite && !done && (
          <>
            <p className="text-sm text-text-secondary mb-5" data-testid="accept-invite-subtitle">
              {t('staffInvite.invitedBy', { name: state.invite.invited_by_name, practice: state.invite.practice_name })}
            </p>
            <div className="rounded-md bg-background/60 border border-border p-3 mb-5 text-sm">
              <p><strong>{state.invite.first_name} {state.invite.last_name}</strong></p>
              <p className="text-text-secondary text-xs">{state.invite.email} · {state.invite.role === 'admin' ? t('staffInvite.roleAdmin') : t('staffInvite.roleAssistant')}</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('passwordReset.newPassword')}</label>
                <input type="password" className="so-input" required value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="accept-invite-pwd"/>
                <p className="text-xs text-text-secondary mt-1">{t('passwordReset.minChars')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('passwordReset.confirmPassword')}</label>
                <input type="password" className="so-input" required value={pwd2} onChange={(e) => setPwd2(e.target.value)} data-testid="accept-invite-pwd2"/>
              </div>
              {err && <p className="text-sm text-danger" data-testid="accept-invite-form-error">{err}</p>}
              <button type="submit" disabled={submitting} className="so-btn so-btn-primary w-full" data-testid="accept-invite-submit">
                {submitting ? t('passwordReset.saving') : t('staffInvite.acceptCta')}
              </button>
              <Link to="/login" className="block text-center text-accent text-sm">{t('passwordReset.backToLogin')}</Link>
            </form>
          </>
        )}

        {done && (
          <div className="rounded-md bg-success/10 border border-success/30 p-4" data-testid="accept-invite-success">
            <p className="text-sm font-semibold text-success">{t('staffInvite.successTitle')}</p>
            <p className="text-xs text-text-secondary mt-1">{t('staffInvite.successText')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
